import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { isObservable, Observable, of } from 'rxjs';
import { AppConfigService } from '@tremaze/shared/util-app-config';
import { normalizeUrl } from '@tremaze/shared/util-http';
import { first, switchMap } from 'rxjs/operators';
import { LanguageService } from '@tremaze/shared/i18n';

@Injectable({ providedIn: 'root' })
export class BaseInterceptor implements HttpInterceptor {
  constructor(
    private appConfigService: AppConfigService,
    private injector: Injector
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // i18n-Assets sollen nicht über die API geladen werden
    const isI18nRequest = request.url.includes('/assets/i18n/') || request.url.includes('assets/i18n/');
    if (isI18nRequest) {
      return next.handle(request);
    }

    const basePath$ = isObservable(this.appConfigService.basePath)
      ? this.appConfigService.basePath
      : of(this.appConfigService.basePath);
    return basePath$.pipe(
      first(),
      switchMap((basePath) => {
        if (!request.url.startsWith('http')) {
          basePath = normalizeUrl(basePath);
          let url = request.url;
          if (!url.startsWith('/')) {
            url = `/${url}`;
          }
          request = request.clone({ url: normalizeUrl(`${basePath}${url}`) });
        }

        // Add 'lang' query parameter to GET requests (but not for i18n files)
        if ((request.method === 'GET' || request.url.includes('getAllEventsBetweenDateFromInst')) && !isI18nRequest) {
          // Lazy inject LanguageService to avoid circular dependency
          const languageService = this.injector.get(LanguageService);
          const currentLang = languageService.currentLanguage;
          const locale = this.mapLanguageToLocale(currentLang);

          request = request.clone({
            setParams: { lang: locale }
          });
        }

        return next.handle(request);
      })
    );
  }

  private mapLanguageToLocale(language: string): string {
    const localeMap: { [key: string]: string } = {
      'de': 'de-DE',
      'en': 'en-US',
      'ar': 'ar-SA',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'it': 'it-IT',
      'pl': 'pl-PL',
      'ro': 'ro-RO',
      'ru': 'ru-RU',
      'tr': 'tr-TR',
      'uk': 'uk-UA'
    };
    return localeMap[language] || 'de-DE';
  }
}
